// Knowledge › Builder + Prompts (A2 step B, plan docs/superpowers/plans/2026-10-10-knowledge-b-builder.md).
// Ported from chatbot_front hub/buildForm.js, discoverForm.js and promptConfigForm.js. No imports on purpose:
// `node --test` loads this file directly (ui/tests/builderForm.test.ts).
//
// The hub route sets system / botId and merge mode itself (backend knowledge-builder.service.ts), so nothing here
// carries a bot.

/** The tasks a build runs, in pipeline order (not eval / latin_glossary, which a build does not run). */
export const BUILD_TASKS = ['select', 'extract', 'chunk', 'genq'] as const;
/** Rendering thousands of checkboxes is slow and unreadable: the list is capped, the selection is not. */
export const MAX_RENDERED = 300;

export interface InlineDoc {
  name: string;
  content: string;
}

export function hasAtLeastOneSource(f: { text: string; files: InlineDoc[]; urls: string[]; removeUrls: string[] }): boolean {
  const hasText = !!f.text.trim();
  const hasFiles = f.files.length > 0;
  const hasUrls = f.urls.some((u) => u && u.trim());
  // A removal-only build is valid: "drop this source, keep everything else".
  return hasText || hasFiles || hasUrls || f.removeUrls.length > 0;
}

/**
 * The build request body. Empty fields are left out, and so are models nobody picked: llm-tester hashes the request
 * into the job's idempotency key, so an unchanged build must send a byte-identical payload.
 */
export function toBuildPayload(f: {
  companyName: string;
  urls: string[];
  text: string;
  files: InlineDoc[];
  prompt: string;
  /** What the bot's chunking runs today (its override, else the default): an unchanged prompt is not sent. */
  effectivePrompt?: string;
  maxChunks: number;
  models: Record<string, string>;
  removeUrls: string[];
}): Record<string, unknown> {
  const documents: InlineDoc[] = [];
  if (f.text.trim()) documents.push({ name: 'pasted-text', content: f.text });
  for (const d of f.files) documents.push({ name: d.name, content: d.content });
  const picked: Record<string, string> = {};
  for (const [task, model] of Object.entries(f.models)) if (model) picked[task] = model;
  const out: Record<string, unknown> = {};
  if (f.companyName.trim()) out.companyName = f.companyName.trim();
  out.urls = f.urls.map((u) => u.trim()).filter(Boolean);
  out.documents = documents;
  // Sent only when edited: a per-build prompt outranks the bot's saved one in llm-tester, so sending the seeded text
  // would pin today's prompt into the job (and its promptHash) instead of following the bot's setting.
  if (f.prompt.trim() && f.prompt.trim() !== (f.effectivePrompt ?? '').trim()) out.prompt = f.prompt;
  out.max_chunks = f.maxChunks;
  if (Object.keys(picked).length) out.models = picked;
  if (f.removeUrls.length) out.removeUrls = f.removeUrls;
  return out;
}

export interface JobRow {
  id: string;
  type: string;
  intent: string | null;
  detail: Record<string, unknown> | null;
  status: string;
  isActive: boolean;
  cancellable: boolean;
  isPaused: boolean;
  pausedReason: string | null;
  pausedData: Record<string, unknown> | null;
  resumeAt: string | null;
  usage: Record<string, unknown> | null;
  models: { model?: string }[];
  cancelRequested: boolean;
  createdAt: string | null;
  error: string | null;
}

const ACTIVE = ['pending', 'running'];

/** One `GET /chunk/jobs` row, normalised (camel / snake case both accepted, as v1). */
export function mapJobRow(job: Record<string, any>): JobRow {
  const type = job.jobType ?? job.job_type ?? '';
  const status = String(job.status ?? '');
  const active = ACTIVE.includes(status);
  return {
    id: String(job.jobId ?? job.id ?? ''),
    type,
    intent: job.intent ?? null,
    detail: job.detail ?? null,
    status,
    isActive: active,
    // An eval run never reads the cancel flag; a Cancel there would look broken.
    cancellable: active && type !== 'eval',
    // Paused (usage limit or a chunk-count question) is in flight but offers Resume / Approve, not Cancel.
    isPaused: status === 'paused',
    pausedReason: job.pausedReason ?? job.paused_reason ?? null,
    pausedData: job.pausedData ?? job.paused_data ?? null,
    resumeAt: job.resumeAt ?? job.resume_at ?? null,
    usage: job.usage ?? null,
    models: Array.isArray(job.models) ? job.models : [],
    // Cancel is cooperative: the flag is set at once, the status flips at the next step boundary.
    cancelRequested: !!(job.cancelRequested ?? job.cancel_requested),
    createdAt: job.createdAt ?? job.created_at ?? null,
    error: job.error ?? null,
  };
}

/** The active snapshot's entries → the sources the bot has now. */
export function sourcesFromSnapshot(detail: { entries?: unknown } | null | undefined): { url: string; sourceId?: string }[] {
  const entries = detail && Array.isArray(detail.entries) ? (detail.entries as Record<string, any>[]) : [];
  return entries.filter((e) => e && e.url).map((e) => ({ url: String(e.url), sourceId: e.sourceId }));
}

// ── discover ────────────────────────────────────────────────────────────────

export function toDiscoverPayload(f: { url: string; includePaths: string }): Record<string, unknown> {
  return {
    url: f.url.trim(),
    includePaths: f.includePaths
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean),
  };
}

export function filterCandidates(urls: string[], query: string): string[] {
  const q = query.trim().toLowerCase();
  return q ? urls.filter((u) => u.toLowerCase().includes(q)) : urls;
}

/** Add picked candidates to the URL list, dropping blanks and duplicates (a re-discover cannot double them). */
export function mergeSelectedIntoUrls(existing: string[], selected: string[]): string[] {
  const out = existing.map((u) => u.trim()).filter(Boolean);
  const seen = new Set(out);
  for (const u of selected) {
    const v = u.trim();
    if (v && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

/** Operator copy for llm-tester's discover warning codes; unknown codes fall back to its own English text. */
export function warningText(code: string | null | undefined, count: number, fallback: string | null | undefined): string | null {
  if (code === 'no_sitemap') return 'No sitemap found — the site may render its menu with JavaScript. Add page addresses by hand.';
  if (code === 'low_yield') return `Only ${count} page(s) found. If the site is larger, its menu is probably built in the browser — add addresses by hand.`;
  if (code === 'truncated') return fallback || 'Some deeper pages were cut off — narrow the paths.';
  return fallback || null;
}

// ── prompts (llm-tester /prompt/config rows) ─────────────────────────────────

export interface PromptRow {
  task: string;
  title?: string;
  default?: string;
  override?: string | null;
  source?: string;
  defaultModel?: string | null;
  overrideModel?: string | null;
  effectiveModel?: string | null;
  modelSource?: string;
  tools?: string[];
  allowedModels?: string[];
}

const rowOf = (rows: PromptRow[], task: string) => rows.find((r) => r.task === task);

/** The task's text as it runs: the bot's override, else the default. */
export function textForTask(rows: PromptRow[], task: string): string {
  const r = rowOf(rows, task);
  if (!r) return '';
  return r.override != null ? r.override : r.default || '';
}

export function hasOverride(rows: PromptRow[], task: string): boolean {
  const r = rowOf(rows, task);
  return !!(r && r.override != null && r.override !== '');
}

export function differsFromDefault(rows: PromptRow[], task: string, text: string): boolean {
  return text.trim() !== String(rowOf(rows, task)?.default ?? '').trim();
}

export function modelForTask(rows: PromptRow[], task: string): string {
  return rowOf(rows, task)?.effectiveModel || '';
}

export function allowedModelsFor(rows: PromptRow[], task: string): string[] {
  const r = rowOf(rows, task);
  return r && Array.isArray(r.allowedModels) ? r.allowedModels : [];
}

export function modelDiffersFromDefault(rows: PromptRow[], task: string, model: string): boolean {
  return model !== String(rowOf(rows, task)?.defaultModel ?? '');
}

/**
 * What to PUT: only what differs from the default. Sending the editor's text unconditionally would save today's
 * default as a permanent per-bot override when only the model changed — freezing that bot against later prompt
 * improvements, invisibly (v1 promptConfigForm). llm-tester COALESCEs, so an omitted field keeps what is stored.
 */
export function toOverridePayload(rows: PromptRow[], task: string, text: string, model: string): { body?: string; model?: string } {
  const out: { body?: string; model?: string } = {};
  if (differsFromDefault(rows, task, text)) out.body = text;
  if (modelDiffersFromDefault(rows, task, model)) out.model = model;
  return out;
}

/**
 * How to save a task: PUT only what differs from the default — and when a stored override is being dropped (put back
 * to the default), DELETE first, because llm-tester COALESCEs the fields a PUT omits and would keep the old one.
 */
export function promptSavePlan(
  rows: PromptRow[],
  task: string,
  text: string,
  model: string,
): { reset: boolean; put: { body?: string; model?: string } | null } {
  // A blank editor means "the default" (llm-tester refuses an empty body).
  const payload = toOverridePayload(rows, task, text.trim() ? text : String(rowOf(rows, task)?.default ?? ''), model);
  const row = rowOf(rows, task);
  const dropsBody = hasOverride(rows, task) && !('body' in payload);
  const dropsModel = !!row?.overrideModel && !('model' in payload);
  return { reset: dropsBody || dropsModel, put: Object.keys(payload).length ? payload : null };
}

export const KNOWLEDGE_TABS = ['documents', 'builder', 'pages', 'snapshots', 'glossary', 'blocks', 'prompts'] as const;
export type KnowledgeTab = (typeof KNOWLEDGE_TABS)[number];

/** The Knowledge page's tab from `?tab=`; anything unknown opens Documents. */
export function knowledgeTab(param: string | null | undefined): KnowledgeTab {
  return (KNOWLEDGE_TABS as readonly string[]).includes(String(param)) ? (param as KnowledgeTab) : 'documents';
}
